import type { MaybeRefOrGetter, Ref } from 'vue';
import { ref, toValue } from 'vue';

import { t } from '@/i18n';
import { HANDLED_ERROR_MESSAGE } from '@/modules/common/constants/errorMessages';
import type { DataDispatcher } from '@/modules/common/state/types';
import type { UsePageControllerResult } from '@/modules/common/state/usePageController';

// Resolved where it is shown, not at import: this module is evaluated once, so a plain constant
// would pin the description to the boot language while the title beside it followed the switch.
const unexpectedDescription = (): string => t('modules.shared.unexpectedErrorDescription');

export type UsePageSubmitOptions = Readonly<{
  pageController: UsePageControllerResult;
  /** Page is dirty if any owned save surface is dirty. */
  isDirty: Readonly<Ref<boolean>>;
  /**
   * Page-specific save body: validate the owned surfaces, run the mutation(s),
   * toast on success. Return `false` on a validation failure; after surfacing a
   * mutation error, throw `HANDLED_ERROR_MESSAGE` so it is swallowed here.
   */
  run: () => Promise<boolean>;
  unexpectedTitle: MaybeRefOrGetter<string>;
  logTag?: string;
}>;

/**
 * Shared page-save scaffold for multi-surface detail pages (AdGroup, CreativeGroup,
 * TargetGroup, Summary). Owns the single-flight re-entry guard, the synchronous
 * error-panel clear, and the `HANDLED_ERROR_MESSAGE` sentinel swallow; exposes the
 * page as one `DataDispatcher` whose `isPending` spans the WHOLE run — validation
 * and mutation. Each page supplies `isDirty` and the page-specific `run`.
 */
// TODO: Improve design of this composable - it maybe improved (and think: why this composable npt in ../composables & should it use useSafeAction?)
export const usePageSubmit = ({
  pageController,
  isDirty,
  run,
  unexpectedTitle,
  logTag = '[usePageSubmit]',
}: UsePageSubmitOptions): DataDispatcher => {
  const isInFlight = ref(false);

  const submit = async (): Promise<boolean> => {
    // Re-entry guard: a programmatic Save (Cmd-S) re-firing before the first run
    // settles would double-run. Checked before the panel clear, so a re-entrant
    // call is a silent no-op.
    if (isInFlight.value) {
      return false;
    }

    // Synchronous clear BEFORE the await — prevents a deferred watcher from
    // clobbering freshly-pushed validation errors.
    pageController.errors.clear();
    isInFlight.value = true;

    try {
      return await run();
    } catch (err) {
      const isHandled = err instanceof Error && err.message === HANDLED_ERROR_MESSAGE;
      if (!isHandled) {
        console.error(`${logTag} unexpected run error`, err);
        pageController.errors.showErrorItem({
          title: toValue(unexpectedTitle),
          description: unexpectedDescription(),
        });
      }
      return false;
    } finally {
      isInFlight.value = false;
    }
  };

  return {
    state: { isDirty, isPending: isInFlight },
    actions: { submit },
  };
};
